/**
 * activityFeed.ts — the rules the CRM Activities page and the CRM dashboard share, now
 * that both read the `activities` table the record drawers write.
 *
 * ADDED 2026-10-30 (Lis, CRM fix batch 2, item 5). Activities lived in two tables. The
 * record drawers' composer (ActivityComposer → lib/activities.ts) writes `activities`,
 * keyed by module_key + record_id; the Activities page and the dashboard read and wrote
 * only the legacy `crm_activities`. So an activity logged on a contact, company, deal or
 * lead never reached either page. Both now read `activities`; `crm_activities` is left
 * alone (RecordActivityTimeline still folds its rows in, read-only).
 *
 * What the old table had and this one does not, and the rule chosen for each:
 *   • activity_date → the activity's DATE is `due_at ?? created_at`: a dated activity is
 *     when it is due or happens, an undated one is when it was logged (`activityDate`).
 *   • completed (bool) → `completed_at` set (done) or null (open).
 *   • OVERDUE → open AND it has a due date AND that date is past (`isActivityOverdue`).
 *     No due date = never overdue: the moment something was logged is not a deadline.
 *   • contact_id / deal_id → ONE record per activity (module_key + record_id). The form
 *     refuses to save without one — an activity about nothing would show on no record.
 *   • owner_id → actor_id, shown as "Logged by". createActivity sets it to the signed-in
 *     person; the form never sends it.
 *   • duration_minutes → dropped. `activities` has no column for it.
 *
 * Pure functions only — nothing here imports the data client, so the jest test runs with
 * no backend. The fetching lives in lib/activities.ts (`fetchActivityRecordNames`) and in
 * the two pages.
 */

import type { ActivityKind } from './activities';

// ─── The four kinds of record an activity can be about ──────────────────────

/** The CRM modules whose records carry activities. Same keys the field engine uses. */
export type ActivityRecordModule = 'crm_contacts' | 'crm_companies' | 'crm_deals' | 'crm_leads';

/** In the order the form offers them. */
export const ACTIVITY_RECORD_MODULES: { key: ActivityRecordModule; label: string }[] = [
  { key: 'crm_contacts',  label: 'Contact' },
  { key: 'crm_companies', label: 'Company' },
  { key: 'crm_deals',     label: 'Deal' },
  { key: 'crm_leads',     label: 'Lead' },
];

/** The same four keys as a list — what the pages pass to `.in('module_key', …)`. */
export const ACTIVITY_RECORD_MODULE_KEYS: ActivityRecordModule[] =
  ACTIVITY_RECORD_MODULES.map(m => m.key);

export const isActivityRecordModule = (key: string): key is ActivityRecordModule =>
  (ACTIVITY_RECORD_MODULE_KEYS as string[]).includes(key);

/** "Contact" / "Company" / "Deal" / "Lead"; "Record" for a key outside the four. */
export const recordModuleLabel = (moduleKey: string): string =>
  ACTIVITY_RECORD_MODULES.find(m => m.key === moduleKey)?.label ?? 'Record';

// ─── Date and overdue ───────────────────────────────────────────────────────

export interface DatedActivity {
  due_at: string | null;
  created_at: string;
}

/** The activity's date: its due date when it has one, else when it was logged. */
export const activityDate = (a: DatedActivity): string => a.due_at ?? a.created_at;

/**
 * Overdue = still open, has a due date, and that date is already past. Completed is never
 * overdue, and neither is an activity with no due date.
 */
export function isActivityOverdue(
  a: { due_at: string | null; completed_at: string | null },
  now: Date = new Date(),
): boolean {
  if (a.completed_at || !a.due_at) return false;
  const due = new Date(a.due_at).getTime();
  return !Number.isNaN(due) && due < now.getTime();
}

/**
 * Is the activity's date on or after `sinceIso`? The dashboard's "this week" and "last 30
 * days". Compared as instants, not strings: the server may answer `+00:00` where the
 * bound says `Z`, and a string compare would then disagree with the clock.
 */
export function isActivityOnOrAfter(a: DatedActivity, sinceIso: string): boolean {
  return new Date(activityDate(a)).getTime() >= new Date(sinceIso).getTime();
}

/**
 * The server-side half of `isActivityOnOrAfter`, for `.or(…)`. A SUPERSET on purpose —
 * due on/after the bound OR logged on/after it — because "due_at, else created_at" is one
 * comparison too many for a filter. The caller applies `isActivityOnOrAfter` to the rows.
 */
export const activityDateSinceFilter = (sinceIso: string): string =>
  `due_at.gte.${sinceIso},created_at.gte.${sinceIso}`;

/**
 * What a row is called in a list. The page's form always writes a subject, but the record
 * drawers' composer lets a note go without a title, so a row may only have a body.
 */
export function activityHeadline(a: { title: string | null; body: string | null }): string {
  const title = (a.title ?? '').trim();
  if (title) return title;
  const firstLine = (a.body ?? '').trim().split('\n')[0].trim();
  if (firstLine) return firstLine.length > 80 ? `${firstLine.slice(0, 79)}…` : firstLine;
  return 'Untitled activity';
}

// ─── Naming the record behind an activity ───────────────────────────────────

/** Placeholder for a record that no longer exists (or is soft-deleted). */
export const DELETED_RECORD = 'Deleted record';
/** Placeholder when the name lookup itself failed — the record may well exist. */
export const NAME_UNAVAILABLE = 'Name unavailable';

/** Ids per request when naming records — keeps each `in.(…)` URL a few KB. */
export const RECORD_NAME_BATCH = 100;

/** The columns each module is named from (plus `is_deleted`, so a soft-deleted record
 *  reads as deleted rather than under its old name). */
export const RECORD_NAME_SELECT: Record<ActivityRecordModule, string> = {
  crm_contacts:  'id, is_deleted, first_name, last_name',
  crm_companies: 'id, is_deleted, name',
  crm_deals:     'id, is_deleted, title',
  crm_leads:     'id, is_deleted, first_name, last_name, company',
};

/** `crm_deals|<uuid>` — one map for all four tables. */
export const recordKey = (moduleKey: string, recordId: string): string =>
  `${moduleKey}|${recordId}`;

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const fullName = (r: Record<string, unknown>): string =>
  [text(r.first_name), text(r.last_name)].filter(Boolean).join(' ');

const NAMERS: Record<ActivityRecordModule, (r: Record<string, unknown>) => string> = {
  crm_contacts:  r => fullName(r) || 'Unnamed contact',
  crm_companies: r => text(r.name) || 'Unnamed company',
  crm_deals:     r => text(r.title) || 'Untitled deal',
  // A lead's first and last name are both nullable — a lead is often just a company.
  crm_leads:     r => fullName(r) || text(r.company) || 'Untitled lead',
};

/** The display name of one fetched row of `moduleKey`. */
export const recordNameFromRow = (moduleKey: ActivityRecordModule, row: Record<string, unknown>): string =>
  NAMERS[moduleKey](row);

export interface ActivityRecordRef {
  module_key: string;
  record_id: string;
}

/** The distinct record ids to fetch, per module. Rows outside the four modules are skipped. */
export function recordIdsByModule(rows: ActivityRecordRef[]): Map<ActivityRecordModule, string[]> {
  const sets = new Map<ActivityRecordModule, Set<string>>();
  for (const r of rows) {
    if (!r.record_id || !isActivityRecordModule(r.module_key)) continue;
    const ids = sets.get(r.module_key) ?? new Set<string>();
    ids.add(r.record_id);
    sets.set(r.module_key, ids);
  }
  const out = new Map<ActivityRecordModule, string[]>();
  sets.forEach((ids, moduleKey) => out.set(moduleKey, Array.from(ids)));
  return out;
}

/** Split a list into batches of at most `size`. */
export function chunk<T>(xs: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += size) out.push(xs.slice(i, i + size));
  return out;
}

/** Rows as fetched with RECORD_NAME_SELECT, per module. */
export type FetchedRecordRows = Partial<Record<ActivityRecordModule, Record<string, unknown>[]>>;

/**
 * Names keyed by `recordKey`. A soft-deleted row is LEFT OUT on purpose, so its activities
 * read "Deleted record" exactly like those whose record is gone altogether.
 */
export function recordNamesFromRows(fetched: FetchedRecordRows): Map<string, string> {
  const out = new Map<string, string>();
  for (const moduleKey of ACTIVITY_RECORD_MODULE_KEYS) {
    for (const row of fetched[moduleKey] ?? []) {
      if (row.id == null || row.is_deleted === true) continue;
      out.set(recordKey(moduleKey, String(row.id)), recordNameFromRow(moduleKey, row));
    }
  }
  return out;
}

export interface ResolvedRecord {
  /** "Contact" / "Company" / "Deal" / "Lead". */
  kind: string;
  /** The record's name, or one of the two placeholders. */
  name: string;
  /** false when `name` is a placeholder. */
  known: boolean;
  /** The record is gone (or soft-deleted). */
  deleted: boolean;
}

/**
 * Which record an activity is about, for display. A record missing from `names` is
 * DELETED — unless its module's lookup failed (`failedModules`), in which case the name is
 * merely unavailable. Saying "Deleted record" for a lookup that did not answer would be a lie.
 */
export function resolveRecord(
  a: ActivityRecordRef,
  names: Map<string, string>,
  failedModules: Set<string> = new Set<string>(),
): ResolvedRecord {
  const kind = recordModuleLabel(a.module_key);
  const name = names.get(recordKey(a.module_key, a.record_id));
  if (name !== undefined) return { kind, name, known: true, deleted: false };
  if (failedModules.has(a.module_key)) return { kind, name: NAME_UNAVAILABLE, known: false, deleted: false };
  return { kind, name: DELETED_RECORD, known: false, deleted: true };
}

/** One choice in the form's record picker. */
export interface RecordOption {
  id: string;
  name: string;
  /** A second detail to tell two "Jane Smith"s apart: a contact's account, a named lead's company. */
  hint: string | null;
}

/** Picker options from rows fetched for the form (soft-deleted rows dropped). */
export function recordOptions(moduleKey: ActivityRecordModule, rows: Record<string, unknown>[]): RecordOption[] {
  return rows
    .filter(r => r.id != null && r.is_deleted !== true)
    .map(r => ({
      id: String(r.id),
      name: recordNameFromRow(moduleKey, r),
      hint: moduleKey === 'crm_contacts'
        ? text(r.account_name) || null
        : moduleKey === 'crm_leads' && fullName(r)
          ? text(r.company) || null
          : null,
    }));
}

// ─── The page's form → the row it writes ────────────────────────────────────

/** The Activities page's create/edit form. */
export interface ActivityForm {
  type: ActivityKind;
  subject: string;
  body: string;
  /** Which kind of record the activity is about — '' until one is chosen. */
  record_module: ActivityRecordModule | '';
  /** The record itself — '' until one is chosen. */
  record_id: string;
  /** The <input type="datetime-local"> value (LOCAL wall-clock), '' for no date. Saved as `due_at`. */
  date: string;
  completed: boolean;
}

/** Exactly the columns the form writes. No actor (createActivity sets it), no duration. */
export interface ActivityWrite {
  module_key: ActivityRecordModule;
  record_id: string;
  type: ActivityKind;
  title: string | null;
  body: string | null;
  due_at: string | null;
  completed_at: string | null;
}

export type ActivityFormField = 'subject' | 'record' | 'date';

export type ActivityPayloadResult =
  | { ok: true; payload: ActivityWrite }
  | { ok: false; field: ActivityFormField; error: string };

export const SUBJECT_REQUIRED = 'Add a subject or a note.';
export const RECORD_REQUIRED =
  'Choose the contact, company, deal or lead this activity is about. Every activity belongs to a record.';
export const DATE_INVALID = 'That date isn’t valid. Pick it again, or clear it.';

/**
 * The form → the row to write, or the reason it may not be written.
 *
 *   • subject → `title` (a subject OR a note is required — the drawers' composer rule,
 *     since both now write the same table and a drawer note may have no title);
 *   • the record → `module_key` + `record_id`, REQUIRED;
 *   • the date → `due_at`: local wall-clock → UTC ISO (the input is local, storage is UTC —
 *     `new Date("YYYY-MM-DDTHH:mm")` parses as local); '' → null;
 *   • completed → `completed_at`: kept as it was when the row was already done
 *     (`opts.completedAt`), stamped `now` when it becomes done, null when open.
 */
export function activityPayload(
  form: ActivityForm,
  opts: { now?: Date; completedAt?: string | null } = {},
): ActivityPayloadResult {
  const title = form.subject.trim();
  const body = form.body.trim();
  if (!title && !body) return { ok: false, field: 'subject', error: SUBJECT_REQUIRED };
  if (!isActivityRecordModule(form.record_module) || !form.record_id) {
    return { ok: false, field: 'record', error: RECORD_REQUIRED };
  }
  let dueAt: string | null = null;
  if (form.date) {
    const due = new Date(form.date);
    if (Number.isNaN(due.getTime())) return { ok: false, field: 'date', error: DATE_INVALID };
    dueAt = due.toISOString();
  }
  const now = opts.now ?? new Date();
  return {
    ok: true,
    payload: {
      module_key: form.record_module,
      record_id: form.record_id,
      type: form.type,
      title: title || null,
      body: body || null,
      due_at: dueAt,
      completed_at: form.completed ? (opts.completedAt || now.toISOString()) : null,
    },
  };
}
